/**
 * 类型安全的高性能事件系统
 * 支持同步/异步事件、优先级、批处理等功能
 */
export class TypeSafeEventSystem {
    constructor() {
        this.listeners = new Map();
        this.stats = new Map();
        this.batchQueue = new Map();
        this.batchTimers = new Map();
        this.batchConfigs = new Map();
        this.nextListenerId = 0;
        this.isEnabled = true;
        this.maxListeners = 100; // 每个事件类型的最大监听器数量
    }
    /**
     * 添加事件监听器
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @param config 监听器配置
     * @returns 监听器ID（用于移除）
     */
    on(eventType, handler, config = {}) {
        return this.addListener(eventType, handler, config);
    }
    /**
     * 添加一次性事件监听器
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @param config 监听器配置
     * @returns 监听器ID
     */
    once(eventType, handler, config = {}) {
        return this.addListener(eventType, handler, { ...config, once: true });
    }
    /**
     * 添加异步事件监听器
     * @param eventType 事件类型
     * @param handler 异步事件处理器
     * @param config 监听器配置
     * @returns 监听器ID
     */
    onAsync(eventType, handler, config = {}) {
        return this.addListener(eventType, handler, { ...config, async: true });
    }
    /**
     * 移除事件监听器
     * @param eventType 事件类型
     * @param listenerId 监听器ID
     * @returns 是否成功移除
     */
    off(eventType, listenerId) {
        const listeners = this.listeners.get(eventType);
        if (!listeners)
            return false;
        const index = listeners.findIndex(l => l.id === listenerId);
        if (index === -1)
            return false;
        listeners.splice(index, 1);
        // 如果没有监听器了，清理相关数据
        if (listeners.length === 0) {
            this.listeners.delete(eventType);
            this.stats.delete(eventType);
        }
        return true;
    }
    /**
     * 移除指定事件类型的所有监听器
     * @param eventType 事件类型
     */
    offAll(eventType) {
        this.listeners.delete(eventType);
        this.stats.delete(eventType);
        this.clearBatch(eventType);
    }
    /**
     * 触发事件
     * @param eventType 事件类型
     * @param event 事件数据
     * @returns Promise（如果有异步监听器）
     */
    async emit(eventType, event) {
        if (!this.isEnabled)
            return;
        // 检查是否启用了批处理
        const batchConfig = this.batchConfigs.get(eventType);
        if (batchConfig?.enabled) {
            this.addToBatch(eventType, event);
            return;
        }
        await this.executeEvent(eventType, event);
    }
    /**
     * 同步触发事件（忽略异步监听器）
     * @param eventType 事件类型
     * @param event 事件数据
     */
    emitSync(eventType, event) {
        if (!this.isEnabled)
            return;
        const listeners = this.listeners.get(eventType);
        if (!listeners || listeners.length === 0)
            return;
        const startTime = performance.now();
        const toRemove = [];
        // 按优先级排序
        const sortedListeners = this.sortListenersByPriority(listeners);
        for (const listener of sortedListeners) {
            if (listener.config.async)
                continue; // 跳过异步监听器
            try {
                if (listener.config.context) {
                    listener.handler.call(listener.config.context, event);
                }
                else {
                    listener.handler(event);
                }
                if (listener.config.once) {
                    toRemove.push(listener.id);
                }
            }
            catch (error) {
                console.error(`Error in event handler for ${eventType}:`, error);
            }
        }
        // 移除一次性监听器
        this.removeListeners(eventType, toRemove);
        // 更新统计信息
        this.updateStats(eventType, performance.now() - startTime);
    }
    /**
     * 设置事件批处理配置
     * @param eventType 事件类型
     * @param config 批处理配置
     */
    setBatchConfig(eventType, config) {
        this.batchConfigs.set(eventType, config);
    }
    /**
     * 立即处理指定事件类型的批处理队列
     * @param eventType 事件类型
     */
    flushBatch(eventType) {
        const batch = this.batchQueue.get(eventType);
        if (!batch || batch.length === 0)
            return;
        // 清除定时器
        const timer = this.batchTimers.get(eventType);
        if (timer) {
            clearTimeout(timer);
            this.batchTimers.delete(eventType);
        }
        // 处理批处理事件
        this.processBatch(eventType, batch);
        // 清空队列
        this.batchQueue.delete(eventType);
    }
    /**
     * 获取事件统计信息
     * @param eventType 事件类型（可选）
     * @returns 统计信息
     */
    getStats(eventType) {
        if (eventType) {
            return this.stats.get(eventType) || this.createEmptyStats(eventType);
        }
        return new Map(this.stats);
    }
    /**
     * 重置统计信息
     * @param eventType 事件类型（可选，不指定则重置所有）
     */
    resetStats(eventType) {
        if (eventType) {
            this.stats.delete(eventType);
        }
        else {
            this.stats.clear();
        }
    }
    /**
     * 启用/禁用事件系统
     * @param enabled 是否启用
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
    }
    /**
     * 检查是否有指定事件类型的监听器
     * @param eventType 事件类型
     * @returns 是否有监听器
     */
    hasListeners(eventType) {
        const listeners = this.listeners.get(eventType);
        return listeners ? listeners.length > 0 : false;
    }
    /**
     * 获取指定事件类型的监听器数量
     * @param eventType 事件类型
     * @returns 监听器数量
     */
    getListenerCount(eventType) {
        const listeners = this.listeners.get(eventType);
        return listeners ? listeners.length : 0;
    }
    /**
     * 清空所有事件监听器和数据
     */
    clear() {
        this.listeners.clear();
        this.stats.clear();
        this.clearAllBatches();
    }
    /**
     * 设置每个事件类型的最大监听器数量
     * @param max 最大数量
     */
    setMaxListeners(max) {
        this.maxListeners = max;
    }
    /**
     * 添加监听器的内部实现
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @param config 配置
     * @returns 监听器ID
     */
    addListener(eventType, handler, config) {
        let listeners = this.listeners.get(eventType);
        if (!listeners) {
            listeners = [];
            this.listeners.set(eventType, listeners);
        }
        // 检查监听器数量限制
        if (listeners.length >= this.maxListeners) {
            console.warn(`Maximum listeners (${this.maxListeners}) exceeded for event type: ${eventType}`);
            return '';
        }
        const listenerId = `listener_${this.nextListenerId++}`;
        const listener = {
            handler,
            config: {
                priority: 0,
                ...config
            },
            id: listenerId
        };
        listeners.push(listener);
        // 初始化统计信息
        if (!this.stats.has(eventType)) {
            this.stats.set(eventType, this.createEmptyStats(eventType));
        }
        return listenerId;
    }
    /**
     * 执行事件的内部实现
     * @param eventType 事件类型
     * @param event 事件数据
     */
    async executeEvent(eventType, event) {
        const listeners = this.listeners.get(eventType);
        if (!listeners || listeners.length === 0)
            return;
        const startTime = performance.now();
        const toRemove = [];
        // 按优先级排序
        const sortedListeners = this.sortListenersByPriority(listeners);
        // 分离同步和异步监听器
        const syncListeners = sortedListeners.filter(l => !l.config.async);
        const asyncListeners = sortedListeners.filter(l => l.config.async);
        // 执行同步监听器
        for (const listener of syncListeners) {
            try {
                if (listener.config.context) {
                    listener.handler.call(listener.config.context, event);
                }
                else {
                    listener.handler(event);
                }
                if (listener.config.once) {
                    toRemove.push(listener.id);
                }
            }
            catch (error) {
                console.error(`Error in sync event handler for ${eventType}:`, error);
            }
        }
        // 执行异步监听器
        const asyncPromises = asyncListeners.map(async (listener) => {
            try {
                if (listener.config.context) {
                    await listener.handler.call(listener.config.context, event);
                }
                else {
                    await listener.handler(event);
                }
                if (listener.config.once) {
                    toRemove.push(listener.id);
                }
            }
            catch (error) {
                console.error(`Error in async event handler for ${eventType}:`, error);
            }
        });
        // 等待所有异步监听器完成
        await Promise.all(asyncPromises);
        // 移除一次性监听器
        this.removeListeners(eventType, toRemove);
        // 更新统计信息
        this.updateStats(eventType, performance.now() - startTime);
    }
    /**
     * 按优先级排序监听器
     * @param listeners 监听器数组
     * @returns 排序后的监听器数组
     */
    sortListenersByPriority(listeners) {
        return listeners.slice().sort((a, b) => (b.config.priority || 0) - (a.config.priority || 0));
    }
    /**
     * 移除指定的监听器
     * @param eventType 事件类型
     * @param listenerIds 要移除的监听器ID数组
     */
    removeListeners(eventType, listenerIds) {
        if (listenerIds.length === 0)
            return;
        const listeners = this.listeners.get(eventType);
        if (!listeners)
            return;
        for (const id of listenerIds) {
            const index = listeners.findIndex(l => l.id === id);
            if (index !== -1) {
                listeners.splice(index, 1);
            }
        }
        // 如果没有监听器了，清理相关数据
        if (listeners.length === 0) {
            this.listeners.delete(eventType);
            this.stats.delete(eventType);
        }
    }
    /**
     * 添加事件到批处理队列
     * @param eventType 事件类型
     * @param event 事件数据
     */
    addToBatch(eventType, event) {
        let batch = this.batchQueue.get(eventType);
        if (!batch) {
            batch = [];
            this.batchQueue.set(eventType, batch);
        }
        batch.push(event);
        const config = this.batchConfigs.get(eventType);
        // 如果达到批处理大小，立即处理
        if (batch.length >= config.batchSize) {
            this.flushBatch(eventType);
            return;
        }
        // 设置延迟处理定时器
        if (!this.batchTimers.has(eventType)) {
            const timer = setTimeout(() => {
                this.flushBatch(eventType);
            }, config.delay);
            this.batchTimers.set(eventType, timer);
        }
    }
    /**
     * 处理批处理事件
     * @param eventType 事件类型
     * @param batch 批处理事件数组
     */
    async processBatch(eventType, batch) {
        // 创建批处理事件对象
        const batchEvent = {
            type: eventType,
            events: batch,
            count: batch.length,
            timestamp: Date.now()
        };
        // 触发批处理事件
        await this.executeEvent(`${eventType}:batch`, batchEvent);
    }
    /**
     * 清除指定事件类型的批处理
     * @param eventType 事件类型
     */
    clearBatch(eventType) {
        this.batchQueue.delete(eventType);
        const timer = this.batchTimers.get(eventType);
        if (timer) {
            clearTimeout(timer);
            this.batchTimers.delete(eventType);
        }
    }
    /**
     * 清除所有批处理
     */
    clearAllBatches() {
        this.batchQueue.clear();
        for (const timer of this.batchTimers.values()) {
            clearTimeout(timer);
        }
        this.batchTimers.clear();
        this.batchConfigs.clear();
    }
    /**
     * 更新事件统计信息
     * @param eventType 事件类型
     * @param executionTime 执行时间
     */
    updateStats(eventType, executionTime) {
        let stats = this.stats.get(eventType);
        if (!stats) {
            stats = this.createEmptyStats(eventType);
            this.stats.set(eventType, stats);
        }
        stats.triggerCount++;
        stats.totalExecutionTime += executionTime;
        stats.averageExecutionTime = stats.totalExecutionTime / stats.triggerCount;
        stats.lastTriggerTime = Date.now();
        stats.listenerCount = this.getListenerCount(eventType);
    }
    /**
     * 创建空的统计信息
     * @param eventType 事件类型
     * @returns 空的统计信息
     */
    createEmptyStats(eventType) {
        return {
            eventType,
            listenerCount: 0,
            triggerCount: 0,
            totalExecutionTime: 0,
            averageExecutionTime: 0,
            lastTriggerTime: 0
        };
    }
}
/**
 * 全局事件系统实例
 */
export const GlobalEventSystem = new TypeSafeEventSystem();
/**
 * 事件装饰器 - 用于自动注册事件监听器
 * @param eventType 事件类型
 * @param config 监听器配置
 */
export function EventListener(eventType, config = {}) {
    return function (target, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        // 在类实例化时自动注册监听器
        const initMethod = target.constructor.prototype.initEventListeners || function () { };
        target.constructor.prototype.initEventListeners = function () {
            initMethod.call(this);
            GlobalEventSystem.on(eventType, originalMethod.bind(this), config);
        };
    };
}
/**
 * 异步事件装饰器
 * @param eventType 事件类型
 * @param config 监听器配置
 */
export function AsyncEventListener(eventType, config = {}) {
    return function (target, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        const initMethod = target.constructor.prototype.initEventListeners || function () { };
        target.constructor.prototype.initEventListeners = function () {
            initMethod.call(this);
            GlobalEventSystem.onAsync(eventType, originalMethod.bind(this), config);
        };
    };
}
//# sourceMappingURL=EventSystem.js.map